import { lemonClient, ENDPOINTS, LS_API_CONFIG } from '../config/lemonSqueezy';

export const lemonSqueezyService = {
  // Fetch all products
  async getProducts() {
    console.log('🔍 Fetching Lemon Squeezy products...');
    try {
      const response = await lemonClient.get(ENDPOINTS.PRODUCTS);
      
      // Enhanced logging with detailed product information
      console.log('✅ Products details:', response.data.data.map(product => ({
        id: product.id,
        name: product.attributes.name,
        slug: product.attributes.slug,
        price: product.attributes.price_formatted,
        status: product.attributes.status,
        createdAt: product.attributes.created_at
      })));
      
      return response.data.data;
    } catch (error) {
      console.error('❌ Error fetching products:', {
        message: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  // Fetch a specific product
  async getProduct(productId) {
    console.log('🔍 Fetching product details:', productId);
    try {
      const response = await lemonClient.get(`${ENDPOINTS.PRODUCTS}/${productId}`);
      console.log('✅ Product fetched:', response.data.data);
      return response.data.data;
    } catch (error) {
      console.error('❌ Error fetching product:', {
        productId,
        message: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  // Create a checkout session
  async createCheckout(variantId, userId) {
    console.log('🛍️ Starting checkout creation:', { variantId, userId });
    
    try {
      if (!variantId) throw new Error('Variant ID is required');
      if (!userId) throw new Error('User ID is required');

      const payload = {
        data: {
          type: 'checkouts',
          attributes: {
            custom_price: null,
            product_options: {
              enabled_variants: [parseInt(variantId, 10)],
              redirect_url: `${window.location.origin}/workspace`
            },
            checkout_options: {
              button_color: '#DC2626'
            },
            checkout_data: {
              custom: {
                user_id: userId
              }
            },
            expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString()
          },
          relationships: {
            store: {
              data: {
                type: 'stores',
                id: LS_API_CONFIG.STORE_ID
              }
            },
            variant: {
              data: {
                type: 'variants',
                id: variantId.toString()
              }
            }
          }
        }
      };
      
      console.log('📦 Checkout payload:', payload);
      const response = await lemonClient.post(ENDPOINTS.CHECKOUT, payload);
      console.log('✅ Checkout created:', response.data.data);
      return response.data.data;
    } catch (error) {
      console.error('❌ Checkout error:', error);
      throw error;
    }
  },

  // Get subscription details
  async getSubscription(subscriptionId) {
    console.log('🔍 Fetching subscription:', subscriptionId);
    try {
      const response = await lemonClient.get(`${ENDPOINTS.SUBSCRIPTIONS}/${subscriptionId}`);
      console.log('✅ Subscription fetched:', response.data.data);
      return response.data.data;
    } catch (error) {
      console.error('❌ Error fetching subscription:', {
        subscriptionId,
        message: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  // Cancel subscription
  async cancelSubscription(subscriptionId) {
    console.log('🚫 Cancelling subscription:', subscriptionId);
    try {
      const response = await lemonClient.delete(`${ENDPOINTS.SUBSCRIPTIONS}/${subscriptionId}`);
      console.log('✅ Subscription cancelled:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error cancelling subscription:', {
        subscriptionId,
        message: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  // Fetch variants for a product
  async getVariants(productId) {
    console.log('🔍 Fetching variants for product:', productId);
    try {
      const response = await lemonClient.get(`${ENDPOINTS.PRODUCTS}/${productId}/variants`);
      const variants = response.data.data;
      
      // Log detailed variant information
      console.log('✅ Variants details:', variants.map(variant => ({
        id: variant.id,
        name: variant.attributes.name,
        price: variant.attributes.price_formatted,
        description: variant.attributes.description,
        status: variant.attributes.status,
        sort: variant.attributes.sort
      })));
      
      return variants;
    } catch (error) {
      console.error('❌ Error fetching variants:', {
        productId,
        message: error.message,
        response: error.response?.data
      });
      throw error;
    }
  }
}; 